import type { AiAnalysisState, Entities } from '@/store/slices/aiAnalysisTypes';

type PersistedAiAnalysisRecord = {
  full_state?: Partial<AiAnalysisState> | null;
  entities?: unknown;
  pinned_items?: AiAnalysisState['pinnedItems'] | null;
  suggested_actions?: AiAnalysisState['suggestedActions'] | null;
  activity_feed?: AiAnalysisState['activityFeed'] | null;
};

function hasActions(actions: unknown): actions is Array<unknown> {
  return Array.isArray(actions) && actions.length > 0;
}

function hasPinnedItems(items: unknown): items is Array<unknown> {
  return Array.isArray(items) && items.length > 0;
}

function hasActivityEvents(feed: unknown): boolean {
  return Boolean(
    feed &&
    typeof feed === 'object' &&
    Array.isArray((feed as { events?: unknown[] }).events) &&
    (feed as { events: unknown[] }).events.length > 0
  );
}

function hasMeaningfulEntities(entities: unknown): boolean {
  if (!entities || typeof entities !== 'object') return false;
  const typed = entities as Partial<Entities>;
  return Boolean(
    typed.caller?.name ||
    typed.incident ||
    (typed.people?.length ?? 0) > 0 ||
    (typed.locations?.length ?? 0) > 0 ||
    (typed.objects?.length ?? 0) > 0 ||
    (typed.officers?.length ?? 0) > 0 ||
    (typed.organizations?.length ?? 0) > 0 ||
    (typed.activity?.length ?? 0) > 0 ||
    (typed.policiesAndLaws?.length ?? 0) > 0 ||
    (typed.other?.length ?? 0) > 0
  );
}

export function buildHydratedAiAnalysisState(
  analysisData: PersistedAiAnalysisRecord | null | undefined,
): Partial<AiAnalysisState> {
  if (!analysisData) {
    return {};
  }

  const fullState =
    analysisData.full_state && typeof analysisData.full_state === 'object'
      ? { ...analysisData.full_state }
      : {};

  if (!hasMeaningfulEntities(fullState.entities) && hasMeaningfulEntities(analysisData.entities)) {
    fullState.entities = analysisData.entities as AiAnalysisState['entities'];
  }

  if (!hasPinnedItems(fullState.pinnedItems) && hasPinnedItems(analysisData.pinned_items)) {
    fullState.pinnedItems = analysisData.pinned_items as AiAnalysisState['pinnedItems'];
  }

  if (!hasActions(fullState.suggestedActions) && hasActions(analysisData.suggested_actions)) {
    fullState.suggestedActions = analysisData.suggested_actions as AiAnalysisState['suggestedActions'];
  }

  if (!hasActions(fullState.allSuggestedActions) && hasActions(analysisData.suggested_actions)) {
    fullState.allSuggestedActions = analysisData.suggested_actions as AiAnalysisState['allSuggestedActions'];
  }

  if (!hasActivityEvents(fullState.activityFeed) && hasActivityEvents(analysisData.activity_feed)) {
    fullState.activityFeed = analysisData.activity_feed as AiAnalysisState['activityFeed'];
  }

  if (Object.keys(fullState).length > 0) {
    return fullState;
  }

  return {
    entities: analysisData.entities as AiAnalysisState['entities'] | undefined,
    pinnedItems: analysisData.pinned_items ?? undefined,
    suggestedActions: analysisData.suggested_actions ?? undefined,
    allSuggestedActions: analysisData.suggested_actions ?? undefined,
    activityFeed: analysisData.activity_feed ?? undefined,
  };
}
